/**
 * Feature Flags Utility - Housing Pro (Phase 2: P2.1)
 * 
 * Supports both environment-level global flags and tenant-level dynamic flags.
 */

export interface ModuleFeatures {
    dashboard: { enabled: boolean; label: string };
    properties: { enabled: boolean; label: string };
    certificates: { enabled: boolean; label: string };
    evictions: { enabled: boolean; label: string };
    aml: { enabled: boolean; label: string };
    finance: { enabled: boolean; label: string };
    arrears: { enabled: boolean; label: string };
    team: { enabled: boolean; label: string };
    regulatorPortal: { enabled: boolean; label: string };
    partnerMarket: { enabled: boolean; label: string };
    agreements: { enabled: boolean; label: string };
    evidenceVault: { enabled: boolean; label: string };
    calendar: { enabled: boolean; label: string };
    reports: { enabled: boolean; label: string };
    analysis: { enabled: boolean; label: string };
    communications: { enabled: boolean; label: string };
    propertyGroups: { enabled: boolean; label: string };
    jobs: { enabled: boolean; label: string };
    orders: { enabled: boolean; label: string };
    tenants: { enabled: boolean; label: string };
    legalQa: { enabled: boolean; label: string };
    complianceReportingWidget: { enabled: boolean; label: string };
}

// Global static flags (backwards compatibility)
export const globalFeatureFlags = {
    EVICTIONS_ENABLED: process.env.REACT_APP_FEATURE_EVICTIONS !== 'false',
    FINANCE_ENABLED: process.env.REACT_APP_FEATURE_FINANCE !== 'false',
    ARREARS_ENABLED: process.env.REACT_APP_FEATURE_ARREARS !== 'false',
};

// Internal store for tenant-level flags
let tenantFeatures: Partial<ModuleFeatures> | null = null;

/**
 * Initialize/Update tenant-level features from the backend.
 */
export const setTenantFeatures = (features: Partial<ModuleFeatures>) => {
    tenantFeatures = features;
};

/**
 * Check if a specific module is enabled for the current tenant.
 * Falls back to global flags if tenant flags aren't loaded yet.
 */
export const isModuleEnabled = (moduleKey: keyof ModuleFeatures): boolean => {
    // Global env flags act as hard overrides — if explicitly enabled at build time,
    // they cannot be overridden by tenant-level flags (prevents demo lockout)
    if (moduleKey === 'arrears'   && globalFeatureFlags.ARREARS_ENABLED)   return true;
    if (moduleKey === 'evictions' && globalFeatureFlags.EVICTIONS_ENABLED) return true;
    if (moduleKey === 'finance'   && globalFeatureFlags.FINANCE_ENABLED)   return true;

    if (!tenantFeatures) {
        // No tenant features loaded yet — use global flags
        switch (moduleKey) {
            case 'propertyGroups': return false;
            default: return true;
        }
    }
    const feature = tenantFeatures[moduleKey];
    if (feature && typeof feature.enabled !== 'undefined') {
        return feature.enabled;
    }

    // Default to true for established Phase 1 modules
    return !['propertyGroups', 'orders', 'analysis'].includes(moduleKey);
};

/**
 * Helper to determine if the user is on the "Housing Pro" enterprise plan.
 * Criterion: propertyGroups module is enabled.
 */
export const isHousingPro = (): boolean => {
    return isModuleEnabled('propertyGroups');
};

/**
 * Legacy export for existing components.
 * Will be deprecated in favor of isModuleEnabled() or useFeatures() hook.
 */
export const featureFlags = {
    get EVICTIONS_ENABLED() { return isModuleEnabled('evictions'); },
    get FINANCE_ENABLED() { return isModuleEnabled('finance'); },
    get ARREARS_ENABLED() { return isModuleEnabled('arrears'); },
    get HOUSING_PRO() { return isHousingPro(); },
    get DASHBOARD_ENABLED() { return isModuleEnabled('dashboard'); },
    get PROPERTIES_ENABLED() { return isModuleEnabled('properties'); },
    get CERTIFICATES_ENABLED() { return isModuleEnabled('certificates'); },
    get TENANTS_ENABLED() { return isModuleEnabled('tenants'); },
    get COMMUNICATIONS_ENABLED() { return isModuleEnabled('communications'); },
    get AGREEMENTS_ENABLED() { return isModuleEnabled('agreements'); },
    get AML_ENABLED() { return isModuleEnabled('aml'); },
    get TEAM_ENABLED() { return isModuleEnabled('team'); },
    // Compliance Reporting widget on the main Dashboard — mostly relevant to
    // Housing Pro accounts; toggleable per-tenant from Super Admin > Feature
    // Toggles rather than the personal drag/hide dashboard customisation.
    get COMPLIANCE_REPORTING_WIDGET_ENABLED() { return isModuleEnabled('complianceReportingWidget'); }
};
